// The map the page opens on.
//
// A full-bleed, pannable, zoomable map is what anybody expects when they
// open something that knows where things are, and until now this app had
// no such surface: it opened on a card that apologised for having no
// link. This is the ground everything else sits on.
//
// **Where the tiles come from is a product decision, not a config
// detail.** The usual answer is a public tile CDN, and it would quietly
// undo the sentence printed at the bottom of this page — every viewer's
// IP and every place they look at would go to a third party. Tiles are
// static files, so they belong exactly where the rest of this product's
// static files already are (SERVICES.md: *Hosting serves the static apps
// and can serve the static indexes*). `VITE_MAP_STYLE` therefore points
// at our own origin, and pointing it at somebody else's is a change in
// what the page promises, not a change of URL.
//
// With no style configured the map still works and says so. It draws the
// route graph's own roads around whatever is being looked at — real
// streets, read by byte range off the same static index that does the
// routing — on a plain ground. That is a real map of the area rather than
// a grey rectangle, and it needs no server at all.

import { useEffect, useRef, useState } from "react";
import {
  ROAD_LAYERS,
  classOf,
  detailFor,
  geoAtClient,
  gestureExtent,
  lonSpanFor,
  polyline,
  projector,
  zoomAbout,
  zoomStep
} from "@wayfind/core";
import type { LatLon } from "@wayfind/core";

export interface MapViewport {
  centre: LatLon;
  zoom: number;
}

/** A thing worth drawing on the map, in the product's own vocabulary. */
export interface MapMarker {
  id: string;
  lat: number;
  lon: number;
  /** `live` is the vehicle and is the only thing allowed to be amber. */
  kind: "live" | "mine" | "stop" | "done" | "halt" | "place";
  label?: string;
}

const DEFAULT_CENTRE: LatLon = { lat: 49.6116, lon: 6.1319 };

export interface MapRoad { points: [number, number][]; roadClass: string }

export interface MapExtent {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

// The sketch frames like the app does, and the longitude span is derived
// rather than fixed. It used to be a flat 1.9x the latitude span — "roughly
// 16:9 at this latitude band" — which was only ever right because the
// projection underneath drew a degree of longitude at a degree of
// latitude's scale. Two wrongs held the picture together: correcting one
// without the other would have squashed every sketch. `lonSpanFor` asks
// the canvas how wide it is and the latitude how short its degrees are,
// which is right at every latitude instead of one.
const SKETCH_ASPECT = 640 / 360;
const MIN_SPAN_LAT = 0.0035;
const MAX_SPAN_LAT = 0.35;
const LIMITS = { min: MIN_SPAN_LAT, max: MAX_SPAN_LAT };
const SKETCH_WIDTH = 640;
const SKETCH_HEIGHT = 360;

/** The lat span (degrees) a sketch shows at `zoom`. */
export function sketchSpanLat(zoom: number): number {
  return 506.25 / 2 ** zoom;
}

function zoomOfSpanLat(spanLat: number): number {
  return Math.log2(506.25 / Math.max(spanLat, 1e-6));
}

/** The extent a sketch draws for a viewport. */
export function sketchExtentOf(viewport: MapViewport): MapExtent {
  const spanLat = Math.min(Math.max(sketchSpanLat(viewport.zoom), MIN_SPAN_LAT), MAX_SPAN_LAT);
  const spanLon = lonSpanFor(spanLat, viewport.centre.lat, SKETCH_ASPECT);
  return {
    minLat: viewport.centre.lat - spanLat / 2,
    maxLat: viewport.centre.lat + spanLat / 2,
    minLon: viewport.centre.lon - spanLon / 2,
    maxLon: viewport.centre.lon + spanLon / 2
  };
}

function viewportOfExtent(extentValue: MapExtent): MapViewport {
  return {
    centre: {
      lat: (extentValue.minLat + extentValue.maxLat) / 2,
      lon: (extentValue.minLon + extentValue.maxLon) / 2
    },
    zoom: zoomOfSpanLat(extentValue.maxLat - extentValue.minLat)
  };
}

export function MapView({
  markers,
  roads,
  extent,
  road,
  viewport,
  onViewportChange,
  styleUrl,
  interactive = true
}: {
  markers: MapMarker[];
  /** The basemap: every road in view, out of this device's own graph. */
  roads?: MapRoad[];
  /** The area to frame. Roads arrive by leaf and spill well past it. */
  extent?: MapExtent | null;
  /** A road to trace, already in [lat, lon] order. */
  road?: [number, number][];
  /** Where the user dragged to. Null means "follow whatever `extent` says". */
  viewport?: MapViewport | null;
  /** Null reports "the user let go of the map" — resume following. */
  onViewportChange?: (viewport: MapViewport | null) => void;
  styleUrl?: string | null;
  interactive?: boolean;
}): JSX.Element {
  const holder = useRef<HTMLDivElement | null>(null);
  const map = useRef<unknown>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!styleUrl || !holder.current || map.current) return;
    let live = true;
    (async () => {
      try {
        const maplibre = await import("maplibre-gl");
        if (!live || !holder.current) return;
        const instance = new maplibre.Map({
          container: holder.current,
          style: styleUrl,
          center: [viewport?.centre.lon ?? DEFAULT_CENTRE.lon, viewport?.centre.lat ?? DEFAULT_CENTRE.lat],
          zoom: viewport?.zoom ?? 13,
          interactive,
          // Nothing about this map is allowed to phone home, and
          // MapLibre's default telemetry-ish extras are simply not wanted
          // on a page whose whole claim is that it is quiet.
          attributionControl: { compact: true }
        });
        map.current = instance;
        instance.on("load", () => { if (live) setReady(true); });
        instance.on("error", () => { if (live) setFailed(true); });
        if (onViewportChange) {
          instance.on("moveend", () => {
            const centre = instance.getCenter();
            onViewportChange({ centre: { lat: centre.lat, lon: centre.lng }, zoom: instance.getZoom() });
          });
        }
      } catch {
        if (live) setFailed(true);
      }
    })();
    return () => { live = false; };
  }, [styleUrl]);

  // Markers and the road are drawn by the fallback painter as well, so the
  // two surfaces show the same facts and neither can show more than the
  // other. `SketchMap` is not a placeholder — it is what an operator who
  // has published no tiles actually gets, and it is honest about it.
  if (!styleUrl || failed) {
    return (
      <InteractiveSketch
        markers={markers}
        roads={roads ?? []}
        extent={extent ?? null}
        road={road}
        viewport={viewport ?? null}
        onViewportChange={onViewportChange}
        interactive={interactive}
        noStyle={!styleUrl}
        failed={failed}
      />
    );
  }

  return (
    <div className="mapview">
      <div className="mapview__canvas" ref={holder} />
      {!ready ? <div className="mapview__veil" aria-hidden="true" /> : null}
      <MarkerLayer markers={markers} map={map} ready={ready} />
    </div>
  );
}

/**
 * Markers as DOM, not as a style layer.
 *
 * They carry the product's vocabulary — live, mine, settled, not coming —
 * and those are CSS tokens shared with every other surface in the app. A
 * marker drawn by the map engine would need its colours restated in a
 * style, which is exactly how the one amber rule gets broken by accident.
 */
function MarkerLayer({
  markers,
  map,
  ready
}: {
  markers: MapMarker[];
  map: { current: unknown };
  ready: boolean;
}): JSX.Element | null {
  const placed = useRef<Map<string, { remove(): void }>>(new Map());

  useEffect(() => {
    const instance = map.current as {
      // Narrow structural type: this file never imports maplibre's types
      // eagerly, because the bundle is loaded on demand.
      project(coords: [number, number]): { x: number; y: number };
    } | null;
    if (!ready || !instance) return;
    let live = true;
    (async () => {
      const maplibre = await import("maplibre-gl");
      if (!live) return;
      const current = placed.current;
      for (const [id, marker] of current) {
        if (!markers.some(entry => entry.id === id)) { marker.remove(); current.delete(id); }
      }
      for (const marker of markers) {
        const existing = current.get(marker.id);
        if (existing) { existing.remove(); current.delete(marker.id); }
        const element = document.createElement("div");
        element.className = `mapmark mapmark--${marker.kind}`;
        if (marker.label) element.title = marker.label;
        const placedMarker = new maplibre.Marker({ element })
          .setLngLat([marker.lon, marker.lat])
          .addTo(map.current as never);
        current.set(marker.id, placedMarker);
      }
    })();
    return () => { live = false; };
  }, [markers, ready]);

  return null;
}

/**
 * The sketch, made draggable.
 *
 * The file's own opening line promises "a full-bleed, pannable, zoomable
 * map", and until now only the tiled surface kept it — the fallback,
 * which is what every deployment with no published tiles actually runs,
 * was a picture. This wraps the same pure painter in a hand: one finger
 * or a mouse drags, a wheel or a pinch zooms, and letting go reports the
 * viewport so the app can fetch roads for where the user actually is.
 *
 * Follow-versus-hold is the one piece of policy here: while the user has
 * not touched the map it follows the run (`extent`), and the first drag
 * takes the view away from it — a map that yanks itself back to the van
 * mid-gesture is not a map, it is a screensaver. "Recentre" hands the
 * view back, and reports `null` so the owner knows following resumed.
 */
function InteractiveSketch({
  markers,
  roads,
  extent,
  road,
  viewport,
  onViewportChange,
  interactive,
  noStyle,
  failed
}: {
  markers: MapMarker[];
  roads: MapRoad[];
  extent: MapExtent | null;
  road?: [number, number][];
  viewport: MapViewport | null;
  onViewportChange?: (viewport: MapViewport | null) => void;
  interactive: boolean;
  noStyle: boolean;
  failed: boolean;
}): JSX.Element {
  const frame = useRef<HTMLDivElement | null>(null);
  // The extent mid-gesture, so dragging is smooth without a round trip
  // through the owner. Cleared whenever the owner's viewport lands —
  // which, since it landed from our own report, draws the same picture.
  const [held, setHeldState] = useState<MapExtent | null>(null);
  // Mirrored in a ref so the native wheel listener — which cannot be
  // re-attached per drag frame — always reads the current gesture.
  const heldRef = useRef<MapExtent | null>(null);
  const setHeld = (value: MapExtent | null): void => {
    heldRef.current = value;
    setHeldState(value);
  };
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  const effective: MapExtent | null = held ?? (viewport ? sketchExtentOf(viewport) : extent);

  useEffect(() => {
    setHeld(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport?.centre.lat, viewport?.centre.lon, viewport?.zoom]);

  const gestureOptions = {
    width: SKETCH_WIDTH,
    height: SKETCH_HEIGHT,
    limits: LIMITS,
    // This canvas is letterboxed to fit rather than cropped to cover.
    cover: false
  };

  const report = (value: MapExtent): void => {
    onViewportChange?.(viewportOfExtent(value));
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!interactive || !effective) return;
    frame.current?.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!pointers.current.has(event.pointerId) || !effective) return;
    const rect = frame.current?.getBoundingClientRect();
    if (!rect) return;
    /*
      Pan and pinch are one operation, shared with the console through
      `@wayfind/core`. The pinch used to be centre-anchored on the grounds
      that "precise cursor anchoring buys little at city scale" — but the
      ground under the fingers sliding away is exactly what makes a map
      feel like it is fighting you, and the shared model costs nothing to
      get right. Sorted by pointer id so "first" and "second" mean the
      same finger in both snapshots.
    */
    const ids = [...pointers.current.keys()].sort((a, b) => a - b);
    const before = ids.map(id => pointers.current.get(id)!);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const after = ids.map(id => pointers.current.get(id)!);
    // Composed on the newest extent: fingers outrun React's renders, so
    // reading the rendered one lets a burst of moves overwrite each other.
    // `heldRef` is written synchronously by `setHeld`, so each event in a
    // burst composes on the one before it rather than on whatever the last
    // render happened to see. Fingers outrun React.
    setHeld(gestureExtent(heldRef.current ?? effective, before, after, rect, gestureOptions));
  };

  const onPointerEnd = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!pointers.current.delete(event.pointerId)) return;
    if (pointers.current.size === 0 && held) report(held);
  };

  // React attaches wheel listeners passively at the root, so an onWheel
  // prop cannot stop the page from scrolling under the gesture. A native
  // listener with `passive: false` is the documented way out.
  useEffect(() => {
    const element = frame.current;
    if (!element || !interactive) return;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const base = heldRef.current ?? (viewport ? sketchExtentOf(viewport) : extent);
      if (!base) return;
      /*
        A flat 1.25 per event was fine for a mouse notch and unusable on a
        trackpad, which streams dozens of events carrying a few pixels
        each — half a second of two-finger scroll compounded to hundreds
        of times. `zoomStep` reads how far the wheel was actually turned,
        and the zoom lands on the cursor rather than the middle of the
        frame.
      */
      const rect = element.getBoundingClientRect();
      const { unproject } = projector(base, SKETCH_WIDTH, SKETCH_HEIGHT);
      const anchor = geoAtClient(
        event.clientX, event.clientY, rect,
        SKETCH_WIDTH, SKETCH_HEIGHT, unproject, false
      );
      const next = zoomAbout(base, zoomStep(event), anchor, LIMITS);
      setHeld(next);
      report(next);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, viewport, extent]);

  const away = viewport != null || held != null;

  return (
    <div
      ref={frame}
      className="mapview__hand"
      style={{ touchAction: "none", cursor: interactive ? (pointers.current.size ? "grabbing" : "grab") : undefined, position: "relative", width: "100%", height: "100%" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      <SketchMap
        markers={markers}
        roads={roads}
        extent={effective}
        road={road}
        noStyle={noStyle}
        failed={failed}
      />
      {away && extent && interactive ? (
        <button
          type="button"
          className="ghost-button mapview__recentre"
          // Bottom-left: the sheet owns the right edge and the search box
          // the top, so this is the corner nothing else claims.
          style={{ position: "absolute", left: "0.75rem", bottom: "0.75rem" }}
          onClick={() => {
            setHeld(null);
            onViewportChange?.(null);
          }}
        >
          Recentre
        </button>
      ) : null}
    </div>
  );
}

/**
 * The map with no tiles: the roads the device already holds.
 *
 * Drawn from the same geometry the arrival time is computed over, so it
 * cannot show a road the router does not believe in. It is deliberately
 * not styled to imitate a basemap — a fake-looking map invites people to
 * trust detail that is not there.
 */
function SketchMap({
  markers,
  roads,
  extent,
  road,
  noStyle,
  failed
}: {
  markers: MapMarker[];
  roads: MapRoad[];
  extent: { minLat: number; maxLat: number; minLon: number; maxLon: number } | null;
  road?: [number, number][];
  noStyle: boolean;
  failed: boolean;
}): JSX.Element {
  // The extent is the streets when there are streets: fitting to two
  // markers would zoom a city down to the pair of dots on it.
  const points: [number, number][] = roads.length
    ? roads.flatMap(entry => entry.points)
    : road?.length
      ? road
      : markers.map(marker => [marker.lat, marker.lon]);

  if (points.length === 0) {
    return (
      <div className="mapview mapview--empty">
        <p className="mapview__note">
          {noStyle
            ? "No map tiles are published for this deployment, so nothing is drawn here yet."
            : "The map could not be loaded, so nothing is drawn here."}
        </p>
      </div>
    );
  }

  // Frame the area asked for, not everything that came back. Roads
  // arrive a whole leaf at a time and a leaf is much larger than a
  // viewport, so fitting to them zooms a street map out to a region.
  const lats = points.map(point => point[0]);
  const lons = points.map(point => point[1]);
  const minLat = extent ? extent.minLat : Math.min(...lats);
  const maxLat = extent ? extent.maxLat : Math.max(...lats);
  const minLon = extent ? extent.minLon : Math.min(...lons);
  const maxLon = extent ? extent.maxLon : Math.max(...lons);
  const width = SKETCH_WIDTH;
  const height = SKETCH_HEIGHT;
  const pad = extent ? 0 : 40;
  // Shared with the console, cosine and all: longitude degrees are
  // shorter than latitude degrees, and drawing them at one scale stretched
  // every sketch sideways.
  const { project, widthKm } = projector(
    { minLat, maxLat, minLon, maxLon }, width, height, pad
  );
  const detail = detailFor(widthKm);

  const path = road?.length ? polyline(road, project) : "";

  return (
    <div className="mapview mapview--sketch">
      <svg className="trace basemap" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="The roads this device holds, and what is on them.">
        {/*
          The basemap, drawn once per class from back to front so bigger
          roads sit over smaller ones — which is the only reason a street
          map reads as a hierarchy rather than a mat of equal lines.
        */}
        {ROAD_LAYERS.map(layer => {
          if (!detail.has(layer)) return null;
          const inLayer = roads.filter(entry => classOf(entry.roadClass) === layer);
          if (!inLayer.length) return null;
          return (
            <path
              key={layer}
              className={`basemap__road basemap__road--${layer}`}
              d={inLayer.map(entry => polyline(entry.points, project)).filter(Boolean).join(" ")}
            />
          );
        })}
        {path ? <path className="trace__road" d={path} /> : null}
        {markers.map(marker => {
          const [x, y] = project(marker.lat, marker.lon);
          const className = marker.kind === "live"
            ? "trace__van"
            : marker.kind === "mine"
              ? "trace__stop trace__stop--mine"
              : marker.kind === "done"
                ? "trace__stop trace__stop--done"
                : marker.kind === "halt"
                  ? "trace__stop trace__stop--halt"
                  : "trace__stop";
          return <circle key={marker.id} className={className} cx={x} cy={y} r={marker.kind === "live" ? 7 : 6} />;
        })}
      </svg>
      {failed ? <p className="mapview__note">The map tiles could not be loaded. These are the roads this device holds.</p> : null}
    </div>
  );
}
